<template>
    <div class="segments-page-container">
        <SystemMessage :message="errorMessage" :dismissButton="false" v1 v-if="!!errorMessage" />
        <SystemMessage :message="successMessage"
                       type="success"
                       v1
                       v-if="!!successMessage"
                       @acknowledged="successMessage = ''" />
        <div v-if="loadingApps" class="segments-page-loader">
            <Loader :status="loadingApps" />
        </div>
        <NoDataOverlay :isShown="!loadingApps && !apps"
                       class="no-apps-overlay"
                       mainText="No Applications Defined"
                       mainTextClass="font-1">
            <div slot="subText" class="font-4 no-data-overlay-subtext">
                To get started <router-link to="/applications/new">add a new app</router-link>
            </div>
        </NoDataOverlay>
        <SegmentsSetupLayout v-if="!loadingApps && apps"
                             :loading="segmentsLoading" :empty="!segments || !segments.length"
                             :abEnabled="selectedApp.hasAB"
                             :abValueIsA="testGroupA"
                             @newSegmentClicked="goToNewSegmentPage()">
            <template v-slot:sidebar>
                <AppList
                    :listData="apps"
                    :selectedApp="selectedApp"
                    @itemSelected="appSelected($event.appKey)"
                />
            </template>
            <template v-slot:header>
                <ABAppHeader
                    v-if="selectedApp.hasAB"
                    uniqueId="segments-page-ab-app-header"
                    :platform="selectedApp.platform.toLowerCase()"
                    :appIcon="selectedApp.icon"
                    :appName="selectedApp.name"
                    :appKey="selectedApp.appKey"
                    :isA="testGroupA"
                    @abChanged="abChanged"
                />
                <AppHeader
                    v-else
                    :platform="selectedApp.platform.toLowerCase()"
                    :appIcon="selectedApp.icon"
                    :appName="selectedApp.name"
                    :appKey="selectedApp.appKey"
                />
            </template>
            <template v-slot:content>
                <SegmentList
                    :segmentsData="segments"
                    :placementsData="placements"
                    :disabled="loadingUpdatePositions"
                    :appKey="selectedApp.appKey"
                    :testGroup="testGroup"
                    @removeItem="openDeleteSegmentModal($event)"
                    @reorder="segmentsPositionsChanged($event)"
                />
                <SegmentSummary
                    :placementsData="placements"
                    :supportedAdUnits="appAdUnits"
                    :disabled="loadingUpdatePositions"
                    @showPlacements="showPlacementsModal($event)"
                />
            </template>
        </SegmentsSetupLayout>
    </div>
</template>

<script>
import SegmentsSetupLayout from '@/pages/monetize/segments/SegmentsSetupLayout.vue';
import SegmentList from '@/pages/monetize/segments/segmentList/SegmentList.vue';
import AppHeader from 'components/appHeader/AppHeader.vue';
import ABAppHeader from 'components/appHeader/ABAppHeader.vue';
import AppList from '@/pages/sdkNetworks/appList/AppList.vue';
import SegmentSummary from '@/pages/monetize/segments/segmentList/SegmentSummary.vue';
import monetizeAppService from 'services/monetizeApp';
import Loader from 'components/loader/loader.vue';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';
import NoDataOverlay from 'components/noDataOverlay/noDataOverlay.vue';
import { mapActions, mapMutations, mapGetters } from 'vuex';
import generalConfig from 'services/metaData/generalConfig';
import PlacementsModal from '@/pages/monetize/segments/modals/PlacementsModal.vue';
import { adUnitIds, adUnitsMap } from 'services/utils/adUnitsMap';
import appToSupportedAdUnits from '@/pages/monetize/segments/utils/supportedAdUnits';
import { getHttpErrorMessage } from 'services/metaData/errors';
import http from 'services/utils/http';
import { sortBy } from 'lodash';
import { emitAngularEvent } from 'services/utils/angularUtils';
import { filterArchivedPlacements } from 'monetize/segments/utils/placements';

const segmentsApiPath = `${generalConfig().sspPlatformJsDomain}/api/v1/partners/segments`;
const segmentsApiKey = 'segments';
const placementsApiPath = '/api/rest/v1/placements?splitBy=deliveryGroup';
const placementsApiKey = 'placements';
const activatedProvidersForAppApiPath = '/partners/utils/activatedProvidersForApp?includeSupersonic=false&unit=all';
const activatedProvidersForAppApiKey = 'activatedProvidersForApp';

export default {
    name: 'SegmentsPage',
    props: {
        appKey: String
    },
    data() {
        return {
            loadingApps: true,
            selectedApp: null,
            apps: undefined,
            errorMessage: '',
            dismissibleTitle: 'Enhance the user experience in your app by targeting your ads to specific users!'
                + ' Easily create user segments based on attributes like age or gender and directly apply these rules'
                + ' to your ad delivery.',
            segments: undefined,
            placements: undefined,
            appAdUnits: undefined,
            activatedProviders: undefined,
            segmentsLoading: false,
            successMessage: '',
            loadingUpdatePositions: false
        };
    },
    components: {
        SegmentsSetupLayout,
        SegmentList,
        AppHeader,
        AppList,
        SegmentSummary,
        Loader,
        SystemMessage,
        NoDataOverlay,
        ABAppHeader
    },
    async created() {
        this.setPage({
            pageTitle: 'Segments'
        });
        const segmentSaved = !!this?.$route?.query?.successMessage;

        if (segmentSaved) {
            this.successMessage = 'Your segment was saved successfully';
            await this.$router.replace({
                name: 'segmentsPage',
                params: { appKey: this.appKey }
            });
        }

        try {
            const res = await monetizeAppService.getMonetizeApplications({
                filters: {
                    excludeArchive: true
                },
                extraFields: ',currencyName,conversionRate,adUnits,virtualItem,hasAB,hasBiddingTest,hasQuickAB'
            });
            const { data: { applications } } = res;
            this.apps = applications;
            this.loadingApps = false;
            if (!this?.apps?.length) return;
            const appKeyFromRoute = this.appKey;
            const appKeyFromStore = this.selectedAppKey;
            const appKeyToFind = appKeyFromRoute || appKeyFromStore;
            const selectedApp = this.apps.find((app) => app.appKey === appKeyToFind);
            const selectedAppKey = selectedApp ? selectedApp.appKey : this.apps[0].appKey;
            this.selectAppKey(selectedAppKey);

            await this.appSelected(selectedAppKey, { refreshSegments: segmentSaved });
        } catch (error) {
            this.loadingApps = false;
            this.segmentsLoading = false;
            const noData = error?.response?.data?.code === 404;
            this.errorMessage = noData ? '' : getHttpErrorMessage(error?.response?.data?.error);
        }
    },
    computed: {
        ...mapGetters({
            testGroup: 'apps/getTestGroup',
            selectedAppKey: 'apps/getSelectedAppKey'
        }),
        testGroupA() {
            return this.testGroup === 'A';
        }
    },
    methods: {
        ...mapMutations({
            setPage: 'setPage'
        }),
        ...mapActions({
            getWithCache: 'cache/getWithCache',
            selectAppKey: 'apps/selectAppKey',
            selectTestGroup: 'apps/selectTestGroup'
        }),
        async appSelected(appKey, { refreshSegments = false } = {}) {
            try {
                const selectedApp = this.apps.find((app) => app.appKey === appKey);
                this.selectedApp = selectedApp;
                this.selectAppKey(appKey);

                if (!selectedApp.hasAB && this.testGroup === 'B') {
                    this.selectTestGroup('A');
                }

                this.segmentsLoading = true;
                await this.$router.replace({
                    name: 'segmentsPage',
                    params: { appKey }
                }).catch(() => {});
                await Promise.all([
                    this.getSegments(appKey, refreshSegments),
                    this.getPlacements(appKey),
                    this.getActiveProvidersForApp(appKey)
                ]);
                this.appAdUnits = appToSupportedAdUnits(this.selectedApp, this.activatedProviders);
                this.segmentsLoading = false;
            } catch (error) {
                this.errorMessage = getHttpErrorMessage(error?.response?.data?.error);
            }
        },
        clearSegmentsCache() {
            emitAngularEvent('clearSegmentsCache');
        },
        abChanged(isA) {
            this.selectTestGroup(isA ? 'A' : 'B');
            this.appSelected(this.selectedApp.appKey);
        },
        async getSegments(appKey, refresh = false) {
            if (refresh) {
                this.clearSegmentsCache();
            }

            const response = await this.getWithCache({
                api: segmentsApiKey,
                req: `${segmentsApiPath}?appKey=${appKey}&testGroup=${this.testGroup}`,
                refresh
            });
            this.segments = sortBy(response.data, 'segmentPosition');
        },
        async getPlacements(appKey, refresh = false) {
            const response = await this.getWithCache({
                api: placementsApiKey,
                req: `${placementsApiPath}&appKey=${appKey}&testGroup=${this.testGroup}`,
                refresh
            });
            this.placements = filterArchivedPlacements(response.data);
        },
        async getActiveProvidersForApp(appKey, refresh = false) {
            const response = await this.getWithCache({
                api: activatedProvidersForAppApiKey,
                req: `${activatedProvidersForAppApiPath}&appKey=${appKey}`,
                refresh
            });
            this.activatedProviders = response.data.activatedProviders;
        },
        async goToNewSegmentPage() {
            const testGroup = this.testGroup === 'B' ? 'B' : undefined;
            await this.$router.push({
                name: 'addSegmentsPage',
                params: { appKey: this.selectedApp.appKey, testGroup }
            });
        },
        async goToEditSegmentPage(segmentId) {
            await this.$router.push({
                name: 'editSegmentsPage',
                params: {
                    appKey: this.selectedApp.appKey,
                    segmentId,
                    testGroup: this.testGroup
                }
            });
        },
        showPlacementsModal(adUnitName) {
            const adUnitId = adUnitIds[adUnitName];
            const adUnit = adUnitsMap[adUnitId];
            this.$modal.open({
                component: PlacementsModal,
                title: `${adUnit.displayName} Placements`,
                props: {
                    placements: this.placements[adUnitId],
                    supportsReward: adUnitId === adUnitIds.rewardedVideo
                }
            });
        },
        async deleteSegment(segmentId) {
            const { appKey } = this.selectedApp;
            this.segmentsLoading = true;
            await http
                .delete(`${segmentsApiPath}?segmentId=${segmentId}&appKey=${appKey}&testGroup=${this.testGroup}`);
            await this.getSegments(appKey, true);
            this.segmentsLoading = false;
        },
        openDeleteSegmentModal(segmentId) {
            const segmentToDelete = this.segments.find((segment) => segment.segmentId === segmentId);

            this.$modal.open({
                version: 1,
                type: 'general',
                title: 'Delete Segment',
                okButtonText: 'Yes',
                cancelButtonText: 'No',
                showIcon: false,
                body: `Are you sure you'd like to delete '${segmentToDelete.segmentName}'?`,
                onOK: () => {
                    this.deleteSegment(segmentId);
                }
            });
        },
        findPosition(segmentPositions, segmentId) {
            return segmentPositions.findIndex((segId) => segId === segmentId) + 1;
        },
        async segmentsPositionsChanged({ segmentsPositions }) {
            try {
                this.loadingUpdatePositions = true;
                await http.put(`${segmentsApiPath}/positions`, {
                    appKey: this.appKey,
                    positions: this.segments
                        .filter((segment) => {
                            return segment.segmentPosition !== this.findPosition(segmentsPositions, segment.segmentId);
                        })
                        .map((segment) => ({
                            segmentId: segment.segmentId,
                            position: this.findPosition(segmentsPositions, segment.segmentId)
                        }))
                });
                await this.getSegments(this.appKey, true);
                this.loadingUpdatePositions = false;
            } catch (error) {
                const noData = error?.response?.data?.code === 404;
                this.errorMessage = noData ? '' : getHttpErrorMessage(error?.response?.data?.error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";

.segments-page-container {
    position: relative;
    background: $white;
    display: flex;
    flex-direction: column;
}

.no-apps-overlay.noDataWHeight {
    position: relative;
    min-height: 389px;
}
</style>
